import "@reach/listbox/styles.css";
import classNames from "classnames";
import { AddToFavoritesButton } from "components/AddToFavoriteButton";
import { AttributePanelContentProps } from "components/AttributePanel";
import { AttributePanelGrid } from "components/AttributePanel/AttributePanelGrid";
import { AttributePanelList } from "components/AttributePanel/AttributePanelList";
import { AttributePanelTabbedGrid } from "components/AttributePanel/AttributePanelTabbedGrid";
import { CmsResponsiveImage } from "components/CmsResponsiveImage";
import { IVisualizerPanelOptionProps } from "components/Visualizer";
import urls from "helpers/urls";
import { imagePlaceholder, slugify } from "helpers/utils";
import type {
    AttributeOptionPlusInfo,
    Configurator,
} from "interfaces/products";
import { useRouter } from "next/router";
import { useState } from "react";
import { type Key, VisuallyHidden } from "react-aria";
import {
    Button,
    Dialog,
    DialogTrigger,
    Heading,
    Label,
    Modal,
    ModalOverlay,
} from "react-aria-components";
import { CloudinaryImage } from "react-tiny-cloudinary";

import CaretIcon from "../../public/svg/caret.svg";
import Search from "../../public/svg/search.svg";
import { ButtonPrimary } from "../Clickable/Buttons";

import panelStyles from "../AttributePanel/attribute-panel.module.scss";
import styles from "./visual-configurator.module.scss";

export type Option = Configurator["options"][number];

export type VisualConfiguratorData = Omit<Configurator, "user_interface"> & {
    user_interface: NonNullable<Configurator["user_interface"]>;
    activeOption?: Option;
    setLoading: (loading: boolean) => void;
    displayToggleIDs: Array<Number>;
};

export interface VisualConfiguratorProps {
    pageTitle: string;
    pageID: number;
    configurators: Array<Configurator>;
    selectedOptions: Array<Option>;
    displayToggleIDs: Array<Number>;
    setLoading: (loading: boolean) => void;
    customizable: boolean;
}

export const getSelectedFinishImagePath = (
    selectedOptions: Array<
        AttributeOptionPlusInfo | IVisualizerPanelOptionProps
    >,
    finishImages: {
        [key: number]: string;
    } | null,
) => {
    let idInSelectedFinish = false;
    let selectedFinishImagePath: string = "";

    // First determine if the finish_images object exists and is non-empty
    if (finishImages && Object.keys(finishImages).length > 0) {
        // If so, check for the current option's existence in the configurator's selected options
        idInSelectedFinish = selectedOptions.some((selectedOption) => {
            return (
                finishImages &&
                Object.keys(finishImages).includes(String(selectedOption.id))
            );
        });

        // Determine the selected attribute option for the current finish
        const selectedFinishImageOption = selectedOptions.filter(
            (selectedOption) => {
                return finishImages && finishImages[selectedOption.id];
            },
        );

        if (selectedFinishImageOption[0]) {
            // Set the image path to that value
            selectedFinishImagePath =
                finishImages[selectedFinishImageOption[0].id];
        }
    }

    if (!idInSelectedFinish) {
        return null;
    }

    return selectedFinishImagePath;
};

export const buildVisualizerParams = (
    pageID: number,
    selectedOptions: Option[],
    configurators: Array<Configurator>,
) => {
    let params = `?referID=${pageID}`;
    configurators.filter(isVisualConfigurator).forEach((conf) => {
        // Finds the currently chosen value for each visual configurator dropdown, mimicing the activeOption prop passed in to VisualProductAttributeComponent
        let option = conf.options.find((opt) =>
            selectedOptions.some((opt2) => opt.id === opt2.id),
        );
        if (!option) {
            option = conf.options[0];
        }
        if (option) {
            params = params.concat(`&attr-${option.attribute.id}=${option.id}`);
        }
    });

    return params;
};

const VisualProductAttributeComponent = (props: VisualConfiguratorData) => {
    const router = useRouter();
    const [selectedKey, setSelected] = useState<Key | undefined>(
        props.activeOption?.id,
    );
    const selected = props.options.find((opt) => opt.id == selectedKey);
    const singularGroupNames = [
        ...new Set(props.options.map((opt) => opt.attribute.singular_name)),
    ];

    let PanelContent: (props: AttributePanelContentProps) => React.ReactElement;

    switch (props.user_interface) {
        case "list":
        case "ctc_design":
            PanelContent = AttributePanelList;
            break;
        case "grid":
            PanelContent = AttributePanelGrid;
            break;
        case "tabbed_grid":
            PanelContent = AttributePanelTabbedGrid;
            break;
    }

    return (
        <DialogTrigger>
            <Label>
                {props.display_name} ({props.options.length})
            </Label>
            <Button
                className={classNames({
                    [styles.optionGroup]: true,
                    "al-pdp-vc__panel-trigger": true,
                    [`al-pdp-vc__panel-trigger--${slugify(
                        props.display_name,
                    )}`]: true,
                })}
            >
                {selected &&
                    (selected.primary_image ? (
                        <CloudinaryImage
                            type="fetch"
                            cldId={selected.primary_image.image}
                            cldSrcSet={[80, 160, 240, 320]}
                            crop="pad"
                            aspectRatio="1:1"
                        >
                            <img alt="" sizes="80px" />
                        </CloudinaryImage>
                    ) : (
                        <CmsResponsiveImage
                            cmsImage={{
                                url: imagePlaceholder,
                            }}
                            sizes="200px"
                            cldSrcSet={[80, 160, 240, 320]}
                            cloudinaryProps={{
                                type: "upload",
                            }}
                        />
                    ))}
                <span>{selected?.option || "Select one"}</span>
                <CaretIcon aria-hidden="true" />
            </Button>
            <ModalOverlay isDismissable className={panelStyles.attributePanel}>
                <Modal className={panelStyles.content}>
                    <Dialog>
                        {({ close }) => (
                            <>
                                <header className={panelStyles.header}>
                                    <div className={panelStyles.titleWrapper}>
                                        <Button
                                            className={panelStyles.caretIcon}
                                            slot="close"
                                        >
                                            <CaretIcon />
                                            <VisuallyHidden>
                                                Close Panel
                                            </VisuallyHidden>
                                        </Button>
                                        <Heading level={5} slot="title">
                                            Select a{" "}
                                            {singularGroupNames.join(" or ")}
                                        </Heading>
                                    </div>
                                </header>
                                <PanelContent
                                    compact={
                                        props.user_interface === "ctc_design"
                                    }
                                    aria-label={`Select a ${singularGroupNames.join(" or ")}`}
                                    autoFocus
                                    options={props.options}
                                    items={props.options}
                                    selectionMode="single"
                                    selectedKeys={selected ? [selected.id] : []}
                                    onSelectionChange={(selection) => {
                                        close();
                                        const key = [...selection][0];
                                        if (!key) return;
                                        setSelected(key);
                                        props.setLoading(true);
                                        if (props.activeOption) {
                                            /**
                                             * Clear the previous value. Done separately from
                                             * setting the new value because there might not
                                             * be a new value and the old attribute might not
                                             * be the same as the new (i.e. selecting knob vs)
                                             * lever
                                             */
                                            delete router.query[
                                                props.activeOption.attribute.id
                                            ];
                                        }
                                        if (key) {
                                            const newActiveOption =
                                                props.options.find(
                                                    (opt) => opt.id === key,
                                                );
                                            if (newActiveOption) {
                                                router.query[
                                                    newActiveOption.attribute.id
                                                ] = `${newActiveOption.id}`;
                                            }
                                        }
                                        router
                                            .replace({ query: router.query })
                                            .then(() => {
                                                props.setLoading(false);
                                            });
                                    }}
                                />
                            </>
                        )}
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </DialogTrigger>
    );
};

function isVisualConfigurator(
    conf: Configurator,
): conf is VisualConfiguratorData {
    return !!conf.user_interface;
}

export const VisualConfigurator = ({
    pageTitle,
    pageID,
    configurators,
    selectedOptions,
    displayToggleIDs,
    setLoading,
    customizable,
}: VisualConfiguratorProps) => {
    return (
        <>
            <h1 className={classNames("h3", styles.header)}>{pageTitle}</h1>
            <section aria-label="Configuration options">
                {configurators
                    .filter(isVisualConfigurator)
                    .map((configurator, i) => {
                        const options = configurator.options.map((opt) => {
                            const selectedFinishImage =
                                getSelectedFinishImagePath(
                                    selectedOptions,
                                    opt.finish_images,
                                );
                            if (selectedFinishImage)
                                return {
                                    ...opt,
                                    primary_image: {
                                        image: selectedFinishImage,
                                        title: "",
                                        asset_type: "",
                                    },
                                };
                            else return opt;
                        });
                        return (
                            <VisualProductAttributeComponent
                                key={i}
                                {...configurator}
                                options={options}
                                activeOption={options.find((opt) =>
                                    selectedOptions.some(
                                        (opt2) => opt.id === opt2.id,
                                    ),
                                )}
                                setLoading={setLoading}
                                displayToggleIDs={displayToggleIDs}
                            />
                        );
                    })}
            </section>
            {customizable && (
                <a
                    className={styles.customizeButton}
                    href={urls.visualizer.concat(
                        buildVisualizerParams(
                            pageID,
                            selectedOptions,
                            configurators,
                        ),
                    )}
                >
                    <span>Create My Design</span>
                    <p>
                        Customize this product with your perfect style and
                        finish combinations for both sides of your door.
                    </p>
                    <CaretIcon />
                </a>
            )}
            <div className={styles.buttonContainer}>
                <ButtonPrimary
                    className={`${styles.button} al-pdp-vc__dealer`}
                    href={urls.dealerLocator}
                >
                    Find a Dealer <Search />
                </ButtonPrimary>
                <AddToFavoritesButton
                    className={`${styles.button} al-pdp-vc__a2f`}
                />
            </div>
        </>
    );
};
